import {
  GridColDef,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridRenderCellParams,
  GridSortModel,
  GridValueGetter,
  getGridStringOperators,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import { useUserItems } from 'api/aws/users/useUserItems'
import { useEntitiesFromSearch } from 'api/search'
import GlobalDataGrid from 'components/DataGrid/GlobalDataGrid'
import { customNotContainsOperator } from 'components/DataGrid/operators/customNotContainsOperator'
import { customNumericSortOperator, customSortOperator } from 'components/DataGrid/operators/customSortOperator'
import { getDensityStyles } from 'components/DataGrid/operators/getDensityStyles'
import RenderDateCell from 'components/Deals/grid/cells/RenderDateCell'
import RenderDealNameCell from 'components/Deals/grid/cells/RenderDealNameCell'
import RenderDealStatusCell from 'components/Deals/grid/cells/RenderDealStatusCell'
import RenderDealTypeCell from 'components/Deals/grid/cells/RenderDealTypeCell'
import RenderUserCell from 'components/Deals/grid/cells/RenderUserCell'
import AggregateCurrencyCell from 'components/MTB/grid/cells/AggregateCell'
import dayjs from 'dayjs'
import { useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { User } from 'types'

const TERMINAL_STATUSES = ['Ended', 'Cancelled', 'Replaced', 'Canceled']

const defaultSortModel: GridSortModel = [
  { field: 'creation_timestamp', sort: 'desc' },
  { field: 'name', sort: 'asc' },
]
const defaultFilterModel: GridFilterModel = { items: [], quickFilterValues: [] }

const initialVisibilityState: GridColumnVisibilityModel = {
  deal_uuid: false,
  predecessorId: false,
  successorId: false,
  oldStatus: false,
  SEO: false,
  organizations: false,
  organization: false,
  implementationFee: false,
  lastModified: false,
  contractId: false,
  isGoogleDisplay: false,
  isGoogleSearch: false,
  isLinkedIn: false,
  isMetaAdvertising: false,
  isMicrosoftAdvertising: false,
  isSimplifi: false,
  isSnapchat: false,
  isSpotify: false,
  isTikTok: false,
  isYoutube: false,
  isSeo1: false,
  isSeo2: false,
  isSeo3: false,
  isSeo4: false,
  seoTier: false,
  probability: false,
  weightedTotal: false,
  orgNameReference: false,
  noSignature: false,
  docName: false,
  docStatus: false,
  docTimeCreation: false,
  timeCompleted: false,
  lastDateOfService: false,
  contractedBy: false,
  status: false,
  daysLeft: false,
  __tree_data_group__: false,
}

const ContactDealInfo = () => {
  const { contactId } = useParams()
  const apiRef = useGridApiRef()
  const navigate = useNavigate()

  const { deals, organizations } = useEntitiesFromSearch()
  const { data: users } = useUserItems()

  const formatDateString = (date: any) => {
    if (!date) return ''
    return dayjs.isDayjs(date) ? date.format('MM/DD/YYYY') : dayjs(date).format('MM/DD/YYYY')
  }

  const contactDeals = useMemo(() => {
    if (!Array.isArray(deals) || !contactId) return []
    return deals
      .filter(d => {
        const inContactsArray = Array.isArray(d?.contacts) && d.contacts.includes(contactId)
        const inContactRecipient =
          Array.isArray(d?.recipients?.contactRecipient) &&
          d.recipients.contactRecipient.some((r: any) => r.id === contactId)
        return inContactsArray || inContactRecipient
      })
      .map(deal => {
        const ownerId = Array.isArray(deal?.owner) ? deal.owner[0] : deal?.owner ?? null
        const orgPK = deal?.PK ?? null
        const organization = orgPK ? (organizations ?? []).find(o => o?.PK === orgPK) : null
        return {
          ...deal,
          rowId: deal?.SK,
          owner: (users ?? []).find((u: User) => u?.user_uuid === ownerId),
          organization,
        }
      })
  }, [deals, contactId, users, organizations])

  const densityStyles = getDensityStyles('standard')

  const columnMap: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Deal Name',
      aggregable: false,
      width: 250,
      headerAlign: 'left',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_, row) => row?.name,
      renderCell: (params: GridRenderCellParams) => (
        <RenderDealNameCell params={params} navigate={navigate} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      aggregable: false,
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => (
        <RenderDealTypeCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'dealStatus',
      headerName: 'Deal Status',
      aggregable: false,
      width: 150,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (params: GridValueGetter) => params,
      renderCell: (params: GridRenderCellParams) => <RenderDealStatusCell params={params} />,
    },
    {
      field: 'startDate',
      headerName: 'Start Date',
      aggregable: false,
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: params => (params ? formatDateString(params) : null),
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'endDate',
      headerName: 'End Date',
      aggregable: false,
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: params => (params ? formatDateString(params) : null),
      renderCell: (params: GridRenderCellParams) => <RenderDateCell params={params} densityStyles={densityStyles} />,
    },
    {
      field: 'netTotal',
      headerName: 'Mo. Total',
      aggregable: true,
      type: 'number',
      width: 125,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) =>
        !TERMINAL_STATUSES.includes(row?.dealStatus) ? parseFloat(row?.totals?.monthlyTotal || row?.price || '0') : 0.0,
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'total',
      headerName: 'Total',
      aggregable: true,
      type: 'number',
      width: 100,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customNumericSortOperator,
      valueGetter: (_, row) => {
        const isCsa = ['CSA', 'RCSA'].includes(row?.type) ?? false
        return isCsa
          ? parseFloat((row?.totals?.monthlyTotal || row?.contractTotal) ?? '0')
          : parseFloat((row?.totals?.total || row?.contractTotal) ?? '0')
      },
      renderCell: (params: GridRenderCellParams) => (
        <AggregateCurrencyCell params={params} densityStyles={densityStyles} />
      ),
    },
    {
      field: 'owner',
      headerName: 'Owner',
      aggregable: false,
      width: 75,
      headerAlign: 'center',
      filterOperators: [...getGridStringOperators(), customNotContainsOperator],
      sortComparator: customSortOperator,
      valueGetter: (_value, row): GridValueGetter => row?.owner?.name ?? '',
      renderCell: params => {
        //@ts-ignore
        if (params?.rowNode?.isAutoGenerated) return
        return <RenderUserCell params={params} densityStyles={densityStyles} />
      },
    },
  ]

  return (
    <GlobalDataGrid
      rows={contactDeals}
      columns={columnMap}
      apiRef={apiRef}
      context={'contact_deals'}
      defaultFilterModel={defaultFilterModel}
      defaultVisibilityModel={initialVisibilityState}
      defaultSortModel={defaultSortModel}
      defaultGridDensity={'standard'}
      checkboxSelection={false}
      aggregationModel={{
        total: 'sum',
        netTotal: 'sum',
      }}
      gridContainerSx={{
        position: 'relative',
        display: 'flex',
        maxHeight: '100vh',
        width: '100%',
        justifyContent: 'center',
        transition: 'all 0.3s ease-in-out',
        height: '100%',
      }}
      treeData={false}
    />
  )
}

export default ContactDealInfo
